import { ErrorMessage, Field, Formik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { useAtom } from "@dbeining/react-atom";
import { Form, Image, Stack } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { userAtom } from "../../atoms";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { RequiredFieldMarker } from "../../components/Forms/RequiredFieldMarker";
import { forgotPasswordService } from "../../services";
import { focusFirstFormField } from "../../utils/form/focusFirstFormField";
import { showToastSuccess, showToastError } from "../../utils";

const ForgotPasswordPage = () => {
    let navigate = useNavigate();
    const currentUser = useAtom(userAtom);
    const [email, setEmail] = useState("");
    const [resetSent, setResetSent] = useState(false);

    const formAutoFocusRef = useFormAutoFocus(!resetSent, [resetSent]);

    useEffect(() => {
        if (currentUser) {
            navigate("/", { state: { message: "Ok" } });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="login-page p-3">
            <Helmet>
                <title>Forgot your password?</title>
            </Helmet>

            <div className="login-brand-section">
                <Image src="/images/froom-waste-tracker-logo.svg" className="login-brand-logo h-20rem froom-brand" alt="Froom Waste Tracker" />
                <Image
                    src="/images/waste-tracking-system-strapline.png"
                    alt="Froom waste tracking system"
                    className="login-strapline-image"
                />
            </div>

            <div className="login-form-container">
                {!resetSent ? (
                    <>
                        <h1 className="password-title">Forgot your password?</h1>
                        <p>
                            Enter the email address you use to sign in and we will send you an email with instructions to reset your account.
                        </p>
                        <Formik
                            initialValues={{
                                email: "",
                            }}
                            validationSchema={Yup.object().shape({
                                email: Yup.string().required("email is required"),
                            })}
                            onSubmit={({ email }, { setStatus, setSubmitting }) => {
                                setStatus();
                                forgotPasswordService.create(email).then(
                                    (response) => {
                                        if (response.status !== "Failure") {
                                            setEmail(email);
                                            setResetSent(true);
                                            showToastSuccess("Email sent to your provided email with reset instructions!");
                                        } else {
                                            setSubmitting(false);
                                            showToastError(response.message);
                                            focusFirstFormField(formAutoFocusRef.current);
                                        }
                                    },
                                    (error) => {
                                        setSubmitting(false);
                                        showToastError(error);
                                        focusFirstFormField(formAutoFocusRef.current);
                                    }
                                );
                            }}
                        >
                            {({ errors, status, touched, isSubmitting, handleSubmit }) => (
                                <Form ref={formAutoFocusRef} noValidate onSubmit={handleSubmit}>
                                    <Stack gap={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="email">Email address <RequiredFieldMarker /></Form.Label>
                                            <Field name="email" type="text" className={"form-control" + (errors.email && touched.email ? " is-invalid" : "")} />
                                            <ErrorMessage name="email" component="div" className="invalid-feedback" />
                                        </Form.Group>
                                        <div className="password-button-row">
                                            <button
                                                type="button"
                                                className="btn-secondary-custom"
                                                onClick={() => navigate("/signin")}
                                            >
                                                Return to Sign In
                                            </button>
                                            <button className="btn-primary-custom" disabled={isSubmitting} type="submit">
                                                {isSubmitting ? <LoadingSpinner text="Sending..." /> : "Send Reset Email"}
                                            </button>
                                        </div>
                                        {status && <div className={"alert alert-danger"}>{status}</div>}
                                    </Stack>
                                </Form>
                            )}
                        </Formik>
                    </>
                ) : (
                    <>
                        <h1 className="password-title">Reset instructions sent</h1>
                        <p>
                            Instructions on how to reset your password have been sent to {email}. Check your email and click the link that we have sent you.
                        </p>
                        <p>
                            If you do not receive the password reset message within a few minutes, please check your spam folder or other email filtering tools.
                        </p>
                        <div className="d-flex justify-content-end mt-3">
                            <button
                                type="button"
                                className="btn-primary-custom"
                                onClick={() =>
                                    navigate("/signin", {
                                        replace: true,
                                    })
                                }
                            >
                                Back to sign in
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export { ForgotPasswordPage };
